import { debounce } from "../../components/helpers/_observer-handler";
import { isIosDevices } from "../../common/_constants";
import { adjustFontScale } from "../../components/helpers/_element-handlers";
import { handleNavigationMediaQueryChange } from "./breakpoints";

// Mobile Sidebar Menu
export const toggleSidebarHiddenVisible = (
    buttonSelector,
    sidebarSelector,
    featureMenu,
) => {
    if (!buttonSelector || !sidebarSelector) {
        return;
    }

    if (sidebarSelector.classList.contains("hidden")) {
        sidebarSelector.classList.remove("hidden");
    }

    const toggleSidebarClickHandler = () => {
        sidebarSelector.classList.toggle("translate-x-full");
        sidebarSelector.classList.toggle("translate-x-0");
        document.body.classList.toggle("sidebar-expanded");

        if (isIosDevices()) {
            document.body.classList.toggle("fixed");
        }
    };

    if (!buttonSelector._clickHandlerAdded) {
        buttonSelector.addEventListener("click", toggleSidebarClickHandler);
        buttonSelector._clickHandlerAdded = true;
    }

    adjustFontScale(featureMenu);
};

const toggleSidebarOnDesktop = (sidebar) => {
    const sidebarSelector = document.querySelector(sidebar);

    if (sidebarSelector.length === 0) {
        return;
    }

    if (sidebarSelector.classList.contains("translate-x-0")) {
        sidebarSelector.classList.toggle("translate-x-full");
        sidebarSelector.classList.toggle("translate-x-0");
        sidebarSelector.classList.add("hidden");
        document.body.classList.toggle("sidebar-expanded");

        if (isIosDevices()) {
            document.body.classList.toggle("fixed");
        }
    }
};

const adjustGridRowForMobileMenus = (menuId) => {
    const parentMenuElement = document.querySelector(menuId);

    if (!parentMenuElement) return;

    const numberOfMenuItems = parentMenuElement?.children.length;
    const gridRowsNumber = Math.ceil(numberOfMenuItems / 2);
    parentMenuElement.style.gridTemplateRows = `repeat(${gridRowsNumber}, 1fr)`;
};

export const toggleMobileSidebarMenu = () => {
    const burgerMenu = document.querySelector("#burgerMenu");
    const sidebarMenu = document.querySelector("#sidebarMenu");
    const featuredMenu = document.querySelector("#featured-sports");
    const sidebarCloseButton = document.querySelector("#sidebarCloseButton");
    const primaryMenuLocator = "#sidebarPrimaryMenu";
    const additionalMenuLocator = "#sidebarAdditionalMenu";
    const loginButton = document.querySelector(
        '[data-testid="sidebar--top-header"] #mainLoginButton',
    );
    const profileButton = document.querySelector(
        '[data-testid="sidebar--top-header"] #profileButton',
    );

    handleNavigationMediaQueryChange(
        window.addEventListener(
            "resize",
            debounce(adjustFontScale(featuredMenu)),
        ),
        null,
    );
    adjustGridRowForMobileMenus(primaryMenuLocator);
    adjustGridRowForMobileMenus(additionalMenuLocator);
    toggleSidebarHiddenVisible(burgerMenu, sidebarMenu, featuredMenu);
    toggleSidebarHiddenVisible(sidebarCloseButton, sidebarMenu, featuredMenu);
    toggleSidebarHiddenVisible(loginButton, sidebarMenu, featuredMenu);
    toggleSidebarHiddenVisible(profileButton, sidebarMenu, featuredMenu);
};

export const expandMobileSidebarMenu = () => {
    const sidebarMenuLocator = "#sidebarMenu";

    const mobileSidebarHandler = () => toggleMobileSidebarMenu();

    const desktopSidebarHandler = () => {
        toggleSidebarOnDesktop(sidebarMenuLocator);
    };

    handleNavigationMediaQueryChange(
        mobileSidebarHandler,
        desktopSidebarHandler,
    );
};

const resetMobileNavScrollBehavour = () => {
    const mainNavigation = document.querySelector("#mainNavigation");
    const secondaryMenu = document.querySelector("#secondaryMenu");

    if (mainNavigation.classList.contains("condensed-state"))
        mainNavigation.classList.remove("condensed-state");
    if (mainNavigation.classList.contains("expanded-state"))
        mainNavigation.classList.remove("expanded-state");
    if (secondaryMenu?.classList.contains("condensed-state"))
        secondaryMenu.classList.remove("condensed-state");
    if (secondaryMenu?.classList.contains("expanded-state"))
        secondaryMenu.classList.remove("expanded-state");

    window.removeEventListener("scroll", setMobileNavStates);
};

const isArticlePage = () =>
    Boolean(document.querySelector('[data-page-type="article-page"]'));
const isProloguePage = () =>
    Boolean(document.querySelector('[data-cms-page-type="Listing"]')) ||
    Boolean(document.querySelector('[data-testid="prologue-page"]'));
const isTeamListingPage = () =>
    Boolean(document.querySelector('[data-page-type="team-prologue"]'));
const isNavigationV3 = () =>
    Boolean(document.querySelector("[data-navigation-version='v3']"));
const shouldKeepMobileNavSticky = () =>
    isArticlePage() || isProloguePage() || isTeamListingPage();
const hasDefaultSkin = () =>
    Boolean(document.querySelector("[data-skin='']")) ||
    Boolean(document.querySelector("[data-skin='skin-140th.css']"));

const setNavScrollState = (element, isScrollingDown) => {
    if (!element) return;

    // Go with this flow only for default skin and article/prologue pages as per design requirements.
    // For other skins, the navigation should not be sticky
    if (hasDefaultSkin() && shouldKeepMobileNavSticky()) {
        element.classList.remove("condensed-state");
        element.classList.add("expanded-state");
        return;
    }

    if (isScrollingDown) {
        element.classList.add("condensed-state");
        element.classList.remove("expanded-state");
    } else {
        element.classList.remove("condensed-state");
        element.classList.add("expanded-state");
    }
};

const setSecondaryMenuV3ScrollState = (secondaryMenu, scrollTop) => {
    if (!secondaryMenu) return;

    // Go with this flow only for default skin and article/prologue pages as per design requirements.
    // For other skins, the navigation should not be sticky
    if (hasDefaultSkin() && (isArticlePage() || scrollTop === 0)) {
        secondaryMenu.classList.remove("secondary-nav-v3-condensed");
        secondaryMenu.classList.add("secondary-nav-v3-expanded");
        return;
    }
    if (hasDefaultSkin() && (isProloguePage() || scrollTop === 0)) {
        secondaryMenu.classList.add("secondary-nav-v3-condensed");
        secondaryMenu.classList.remove("secondary-nav-v3-expanded");
        return;
    }

    secondaryMenu.classList.add("secondary-nav-v3-condensed");
    secondaryMenu.classList.remove("secondary-nav-v3-expanded");
};

let mobileLastScrollTop = 0;
const setMobileNavStates = () => {
    const mainNavigation = document.querySelector("#mainNavigation");
    const secondaryMenu = document.querySelector("#secondaryMenu");
    const activeSecondaryMenuDropdown = document.querySelector(
        "#secondaryMenuContent [data-has-dropdown]:not(.opacity-0)",
    );
    const activeSecondaryMenu = document.querySelector(
        "[data-menu-has-dropdown].font-bold",
    );
    const secondaryMenuChevron =
        activeSecondaryMenuDropdown?.parentElement.querySelector(
            "[data-menu-chevron]",
        );

    if (!mainNavigation) return;

    let scrollTop = window.scrollY || document.documentElement.scrollTop;

    const isScrollingDown = scrollTop > mobileLastScrollTop;

    setNavScrollState(mainNavigation, isScrollingDown);
    setNavScrollState(secondaryMenu, isScrollingDown);

    if (!shouldKeepMobileNavSticky() && isScrollingDown) {
        activeSecondaryMenuDropdown?.classList.add(
            "opacity-0",
            "pointer-events-none",
        );
        activeSecondaryMenu?.classList.remove("font-bold");
        secondaryMenuChevron?.classList.add("rotate-180");
    }

    if (isNavigationV3()) {
        setSecondaryMenuV3ScrollState(secondaryMenu, scrollTop);
    }

    mobileLastScrollTop = scrollTop;
};

const mobileNavScrollBehavour = () => {
    const mainNavigation = document.querySelector("#mainNavigation");
    const secondaryMenu = document.querySelector("#secondaryMenu");

    // Go with this flow only for default skin and article/prologue pages as per design requirements.
    // For other skins, the navigation should not be sticky
    if (hasDefaultSkin() && shouldKeepMobileNavSticky()) {
        setNavScrollState(mainNavigation, false);
        setNavScrollState(secondaryMenu, false);
        setSecondaryMenuV3ScrollState(secondaryMenu, 0);
    }

    window.addEventListener("scroll", setMobileNavStates);
};

export const condensedMobileNavigationBar = () => {
    handleNavigationMediaQueryChange(
        mobileNavScrollBehavour,
        resetMobileNavScrollBehavour,
    );
};

// Mobile Edition Menu
export const toggleEditionMenu = (button, buttonArrow, editions) => {
    const buttonSelector = document.querySelector(button);
    const buttonArrowSelector = document.querySelector(buttonArrow);
    const editionsSelector = document.querySelector(editions);

    if (!buttonSelector || !editionsSelector || !buttonArrowSelector) {
        return;
    }

    const editionsContainer = editionsSelector?.firstElementChild;
    const expandedHeight = `${editionsContainer.offsetHeight}px`;
    const collapsedHeight = "0px";
    editionsSelector.style.height = collapsedHeight;

    const toggleeditionClickHandler = () => {
        const currentHeight = editionsSelector.style.height;

        editionsSelector.style.height =
            currentHeight === collapsedHeight
                ? expandedHeight
                : collapsedHeight;

        // Toggle the class for the button
        buttonArrowSelector.classList.toggle("open");
    };

    if (!buttonSelector._clickHandlerAdded) {
        buttonSelector.addEventListener("click", toggleeditionClickHandler);
        buttonSelector._clickHandlerAdded = true;
    }
};

export const expandMobileEditionMenu = () => {
    const editionButtonLocator = "#mobileEditionButton";
    const editionsMenuLocator = "#mobileEditionsMenu";
    const editionButtonArrowLocator = "#mobileEditionButtonArrow";

    const mobileEditionsHandler = () => {
        toggleEditionMenu(
            editionButtonLocator,
            editionButtonArrowLocator,
            editionsMenuLocator,
        );
    };

    handleNavigationMediaQueryChange(mobileEditionsHandler, null);
};

// Mobile Sub Menu Condensed/Expanded State
const touchEventHandler = (secondaryMenu, thirdLevel) => {
    let startX = 0;
    let isSwiping = false;

    const secondaryMenuContainer = document.querySelector(secondaryMenu);
    const thirdLevelContainer = document.querySelector(thirdLevel);
    const thirdLevelTitle = thirdLevelContainer.querySelector("div");

    secondaryMenuContainer.addEventListener("touchstart", (event) => {
        startX = event.touches[0].clientX;
        isSwiping = true;

        thirdLevelTitle.classList.add(
            "transition-all",
            "duration-300",
            "ease-sidebar-menu",
        );
        thirdLevelTitle.style.width = `${thirdLevelTitle.offsetWidth}px`;
    });

    secondaryMenuContainer.addEventListener("touchmove", (event) => {
        if (!isSwiping) return;

        const currentX = event.touches[0].clientX;
        const deltaX = currentX - startX;

        if (deltaX < 0) {
            thirdLevelTitle.style.width = 0;
            thirdLevelTitle.style.opacity = 0;
        }
    });

    secondaryMenuContainer.addEventListener("touchend", () => {
        isSwiping = false;
    });
};

const swipeGestureHandler = () => {
    const subMenuLocator = "#secondaryMenu";
    const subMenuItemsLocator = "#mobileSecondaryNavigation";
    const thirdLevelLocator =
        "#secondaryMenu #secondaryMenuContent #brandLogoAndName";
    const activeSportLogoLocator =
        "#secondaryMenu div #brandLogoAndName #activeSportLogo";

    const mobileSecondaryNavigation =
        document.querySelector(subMenuItemsLocator);
    const activeSportLogo = document.querySelector(activeSportLogoLocator);

    if (!activeSportLogo) return;

    if (mobileSecondaryNavigation) {
        const isHorizontallyScrollable =
            mobileSecondaryNavigation.scrollWidth >
            mobileSecondaryNavigation.clientWidth;

        if (isHorizontallyScrollable) {
            touchEventHandler(subMenuLocator, thirdLevelLocator);
        }
    }
};

export const toggleMobileSubMenuLogo = () => {
    handleNavigationMediaQueryChange(swipeGestureHandler, null);
    handleNavigationMediaQueryChange(
        window.addEventListener("resize", debounce(swipeGestureHandler)),
        null,
    );
};
